import type { SiteObject, SiteObjectKind } from '@/site/SiteObject';
import { totalHeight } from '@/site/SiteObject';
import { formFactorLabel } from './FloraFacets';
import { formatLength } from './dom';
import type { ManifestPlant } from './PlantManifest';
import type { DisplayUnit } from './UiPreferences';

/**
 * Pointers: the scene read as a callout drawing.
 *
 * The plant manifest already knows what every specimen is, and numbers each one. What it cannot do is
 * tell you *which tree in front of you* is number 14 — the report and the model are two documents you
 * hold in your head at once. Pointers closes that: a leader line up from each thing in the scene to a
 * floating header carrying its tag number, its species colour, and its name. Reading the schedule and
 * reading the viewport become the same act.
 *
 * This module is the pure half — targets, tag numbers, and screen-space layout, with no DOM and no
 * THREE. `ScenePointers` projects and draws it. The split is the one `PlantManifest` /
 * `PlantManifestPanel` already uses, and it is what lets the hard part — deciding which of four
 * hundred overlapping labels actually gets drawn — be unit-tested against synthetic coordinates
 * rather than against a camera.
 *
 * ## The numbers are the manifest's numbers
 *
 * A plant's tag here is `ManifestPlant.index`, unchanged. That index is assigned after sorting by
 * species and position (see `compareForNumbering`), so it is a function of the document rather than
 * of insertion order — which is exactly what makes it safe to print in two places at once. Site
 * objects are not in the manifest at all, so they get their own series on the same discipline; see
 * {@link assignObjectTags}.
 */

export type PointerDetail = 'tag' | 'name' | 'full';

/** What the overlay is pointing at: everything, or only what is selected. */
export type PointerScope = 'all' | 'selection';

export type PointerKind = 'plant' | SiteObjectKind;

/** One thing worth a callout, before anything is known about where on screen it lands. */
export interface PointerTarget {
  /** Instance id for a plant, object id for a structure — what a click selects. */
  id: string;
  kind: PointerKind;
  /** `14` for a plant, `B2` / `P1` / `F3` for a site object. */
  tag: string;
  /** The species swatch from the manifest, or a kind colour for a structure. */
  color: string;
  /** Common name, or the object's own name. The line shown at `name` detail and above. */
  title: string;
  /** Classification and size — `Tree · 3.1 × 2.4 m`. Shown at `full` detail. */
  detail: string;
  /** Where the leader line starts: the top of the thing, in world metres. */
  world: { x: number; y: number; z: number };
}

/** A target once the camera has had its say. Screen coordinates are host-relative pixels. */
export interface ProjectedTarget {
  target: PointerTarget;
  anchorX: number;
  anchorY: number;
  /** Metres from the camera — drives both the ordering and the fade. */
  distance: number;
}

export interface LayoutViewport {
  width: number;
  height: number;
}

export interface LayoutOptions {
  detail: PointerDetail;
  viewport: LayoutViewport;
  /** Full headers drawn before the rest degrade to numbered dots. */
  maxHeaders?: number;
  /** Dots drawn before the rest are dropped entirely. */
  maxDots?: number;
}

/** Where one callout ends up. `x`/`y` are the box's top-left corner. */
export interface PointerPlacement {
  target: PointerTarget;
  anchorX: number;
  anchorY: number;
  x: number;
  y: number;
  width: number;
  height: number;
  opacity: number;
  /** True when this degraded to a bare numbered dot because a header would not fit. */
  dot: boolean;
}

/* --------------------------------- Building the targets --------------------------------- */

/**
 * Tag prefixes for the things the plant manifest does not count.
 *
 * `F` for furnishing rather than `P` for prop, because paving has the stronger claim on `P` and
 * "site furnishing" is what a bench, a fire pit, and a lamp post are actually called on a drawing.
 */
const KIND_PREFIX: Record<SiteObjectKind, string> = {
  building: 'B',
  paving: 'P',
  prop: 'F',
};

/** Series order, so a site's buildings are numbered before its paving on every machine. */
const KIND_ORDER: readonly SiteObjectKind[] = ['building', 'paving', 'prop'];

/**
 * Structure colours.
 *
 * Deliberately outside `MANIFEST_PALETTE`: a building is not a species, and giving it a swatch from
 * the planting legend would suggest it belongs to that legend. These read as ink rather than as
 * plot colours.
 */
const KIND_COLOR: Record<SiteObjectKind, string> = {
  building: '#8d94a3',
  paving: '#a4977e',
  prop: '#7f8f9c',
};

const KIND_LABEL: Record<SiteObjectKind, string> = {
  building: 'Building',
  paving: 'Paving',
  prop: 'Furnishing',
};

/**
 * Number the site objects, `B1`, `P2`, `F3`.
 *
 * Sorted by kind then position then id before numbering, exactly as `buildPlantManifest` sorts before
 * assigning its indices: the tag has to depend only on the *document*, or a callout drawing would
 * renumber itself every time a shed was clicked. Hidden objects are numbered along with the rest —
 * dropping them would shift every tag after them the moment something was hidden, which is precisely
 * the instability the sort exists to prevent.
 */
export function assignObjectTags(objects: readonly SiteObject[]): Map<string, string> {
  const ordered = [...objects].sort(compareObjects);
  const counters = new Map<SiteObjectKind, number>();
  const tags = new Map<string, string>();
  for (const object of ordered) {
    const next = (counters.get(object.kind) ?? 0) + 1;
    counters.set(object.kind, next);
    tags.set(object.id, `${KIND_PREFIX[object.kind]}${next}`);
  }
  return tags;
}

function compareObjects(a: SiteObject, b: SiteObject): number {
  const kindDelta = KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind);
  if (kindDelta !== 0) return kindDelta;
  if (a.position.x !== b.position.x) return a.position.x - b.position.x;
  if (a.position.z !== b.position.z) return a.position.z - b.position.z;
  return a.id.localeCompare(b.id);
}

/**
 * A callout target for one placed specimen.
 *
 * `groundY` comes from the live instance rather than from the manifest, which drops `y` because a
 * schedule is read in plan. The overlay needs it back: a label has to float above the plant's crown,
 * and on sculpted ground that height is not a constant.
 */
export function plantPointerTarget(
  plant: ManifestPlant,
  color: string,
  groundY: number,
  unit: DisplayUnit,
): PointerTarget {
  return {
    id: plant.id,
    kind: 'plant',
    tag: String(plant.index),
    color,
    title: plant.commonName,
    detail: `${formFactorLabel(plant.formFactor)} · ${formatLength(plant.heightMeters, unit)} × ${formatLength(plant.spreadMeters, unit)}`,
    world: { x: plant.x, y: groundY + plant.heightMeters, z: plant.z },
  };
}

/**
 * A callout target for a building, a paved area, or a prop.
 *
 * The anchor sits at the top of a building's roof and at the top of a prop, but on the *ground* for
 * paving: a patio has no height to rise from, and a header floating a metre over a flat surface would
 * read as pointing at the air above it.
 */
export function objectPointerTarget(
  object: SiteObject,
  tag: string,
  unit: DisplayUnit,
): PointerTarget {
  const rise =
    object.kind === 'building'
      ? totalHeight(object)
      : object.kind === 'prop'
        ? object.heightMeters
        : 0;
  const size = `${formatLength(object.widthMeters, unit)} × ${formatLength(object.depthMeters, unit)}`;
  return {
    id: object.id,
    kind: object.kind,
    tag,
    color: KIND_COLOR[object.kind],
    title: object.name,
    detail:
      object.kind === 'paving'
        ? `${KIND_LABEL[object.kind]} · ${object.paving} · ${size}`
        : `${KIND_LABEL[object.kind]} · ${size}`,
    world: { x: object.position.x, y: object.position.y + rise, z: object.position.z },
  };
}

/* ------------------------------------- Laying it out ------------------------------------- */

/**
 * How far above the thing the header floats, in pixels.
 *
 * Far enough that the leader reads as a leader rather than as a box sitting on a plant's head; close
 * enough that the eye does not have to search for which of six labels belongs to this one.
 */
const LEADER_LENGTH = 34;

/** Keep callouts off the very edge of the view, where a panel is about to overlap them anyway. */
const EDGE_MARGIN = 10;

/** Breathing room between two placed boxes. Touching labels read as one wide label. */
const BOX_GAP = 3;

/**
 * Header box sizes, per detail level.
 *
 * Estimated from the text rather than measured: measuring would mean laying the element out in the
 * DOM before deciding whether to draw it, which is both a per-frame reflow and a dependency this
 * module exists to avoid. Being a few pixels out costs a slightly generous gap between two labels,
 * which nobody can see; being in the DOM would cost the frame budget, which everybody can.
 */
const HEADER_HEIGHT: Record<PointerDetail, number> = { tag: 22, name: 24, full: 42 };
const CHAR_WIDTH = 6.2;
const CHIP_PADDING = 30;
const BOX_PADDING = 18;

/** A numbered dot: what a header degrades to when the space for it is already taken. */
const DOT_SIZE = 20;

/**
 * The distance band a callout fades across, in metres.
 *
 * Full strength across the working foreground, easing to a floor rather than to nothing: a plant at
 * the back of a large site is still a plant you are counting, and a label that vanished would take
 * it out of the tally without saying so. The floor is dim enough to stay out of the way of the
 * planting you are actually looking at.
 */
const FADE_FULL_METERS = 26;
const FADE_MIN_METERS = 95;
const MIN_OPACITY = 0.34;

/** Defaults sized so a mass planting cannot spend the frame budget annotating itself. */
const DEFAULT_MAX_HEADERS = 80;
const DEFAULT_MAX_DOTS = 400;

/**
 * Nudges tried, in order, when the preferred spot is taken.
 *
 * Upward first, because a callout that has climbed is still obviously attached to the thing below it;
 * sideways only after that, since a label offset horizontally has to be traced back along its leader
 * before it can be trusted.
 */
const LADDER: readonly { dx: number; dy: number }[] = [
  { dx: 0, dy: 0 },
  { dx: 0, dy: -26 },
  { dx: 0, dy: -52 },
  { dx: -72, dy: -14 },
  { dx: 72, dy: -14 },
  { dx: -72, dy: -42 },
  { dx: 72, dy: -42 },
];

interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The header box one target would need, at this detail level. */
export function estimateHeaderSize(target: PointerTarget, detail: PointerDetail): Box {
  const height = HEADER_HEIGHT[detail];
  const chip = CHIP_PADDING + target.tag.length * CHAR_WIDTH;
  if (detail === 'tag') return { x: 0, y: 0, width: chip + 8, height };
  const text = detail === 'full' ? Math.max(target.title.length, target.detail.length) : target.title.length;
  return { x: 0, y: 0, width: chip + text * CHAR_WIDTH + BOX_PADDING, height };
}

/**
 * Place as many callouts as will legibly fit.
 *
 * Nearest-first, and greedy: the thing closest to the camera is the thing the view is about, so it
 * wins any contested patch of screen. Anything that cannot find room degrades to a numbered dot
 * before it is dropped, which is what keeps the overlay *countable* — every plant that is on screen
 * and in scope leaves a mark, even where there is no room to name it.
 *
 * Off-screen and behind-camera targets are dropped by the caller's projection; what arrives here is
 * culled again against the viewport because a label anchored just off the edge still has a box that
 * would hang into view.
 */
export function layoutPointers(
  projected: readonly ProjectedTarget[],
  options: LayoutOptions,
): PointerPlacement[] {
  const { viewport, detail } = options;
  const maxHeaders = options.maxHeaders ?? DEFAULT_MAX_HEADERS;
  const maxDots = options.maxDots ?? DEFAULT_MAX_DOTS;

  const ordered = [...projected].sort((a, b) => a.distance - b.distance);
  const placed: Box[] = [];
  const placements: PointerPlacement[] = [];
  let headers = 0;
  let dots = 0;

  for (const entry of ordered) {
    if (!withinViewport(entry, viewport)) continue;
    const opacity = fadeAt(entry.distance);

    if (headers < maxHeaders) {
      const size = estimateHeaderSize(entry.target, detail);
      const spot = findSpot(entry, size, placed, viewport);
      if (spot) {
        placed.push(spot);
        placements.push({ ...boxPlacement(entry, spot, opacity), dot: false });
        headers += 1;
        continue;
      }
    }

    if (dots >= maxDots) continue;
    const size = { x: 0, y: 0, width: DOT_SIZE, height: DOT_SIZE };
    const spot = findSpot(entry, size, placed, viewport, true);
    if (!spot) continue;
    placed.push(spot);
    placements.push({ ...boxPlacement(entry, spot, opacity), dot: true });
    dots += 1;
  }

  return placements;
}

function boxPlacement(
  entry: ProjectedTarget,
  box: Box,
  opacity: number,
): Omit<PointerPlacement, 'dot'> {
  return {
    target: entry.target,
    anchorX: entry.anchorX,
    anchorY: entry.anchorY,
    x: box.x,
    y: box.y,
    width: box.width,
    height: box.height,
    opacity,
  };
}

/**
 * Walk the ladder for a free box, clamping each candidate into the view before testing it.
 *
 * A dot takes a much shorter leader than a header: it is a marker on the thing rather than a label
 * above it, and floating it as high as a header would put it among the headers it was demoted to
 * make room for.
 */
function findSpot(
  entry: ProjectedTarget,
  size: Box,
  placed: readonly Box[],
  viewport: LayoutViewport,
  dot = false,
): Box | null {
  const lift = dot ? DOT_SIZE : LEADER_LENGTH;
  for (const step of dot ? LADDER.slice(0, 3) : LADDER) {
    const box = clampToViewport(
      {
        x: entry.anchorX - size.width / 2 + step.dx,
        y: entry.anchorY - lift - size.height + step.dy,
        width: size.width,
        height: size.height,
      },
      viewport,
    );
    if (!placed.some((other) => overlaps(box, other))) return box;
  }
  return null;
}

function clampToViewport(box: Box, viewport: LayoutViewport): Box {
  return {
    ...box,
    x: clamp(box.x, EDGE_MARGIN, Math.max(EDGE_MARGIN, viewport.width - box.width - EDGE_MARGIN)),
    y: clamp(box.y, EDGE_MARGIN, Math.max(EDGE_MARGIN, viewport.height - box.height - EDGE_MARGIN)),
  };
}

function overlaps(a: Box, b: Box): boolean {
  return (
    a.x < b.x + b.width + BOX_GAP &&
    a.x + a.width + BOX_GAP > b.x &&
    a.y < b.y + b.height + BOX_GAP &&
    a.y + a.height + BOX_GAP > b.y
  );
}

/**
 * Whether the thing itself is in frame.
 *
 * Tested on the anchor with a generous margin rather than on the box: a plant a little outside the
 * view can still legitimately carry a label that hangs into it, and clipping those out would make
 * the edge of the screen look emptier than the garden is.
 */
function withinViewport(entry: ProjectedTarget, viewport: LayoutViewport): boolean {
  const slack = 120;
  return (
    entry.anchorX >= -slack &&
    entry.anchorX <= viewport.width + slack &&
    entry.anchorY >= -slack &&
    entry.anchorY <= viewport.height + slack
  );
}

/** Opacity for a callout at this distance — full in the foreground, easing to a floor. */
export function fadeAt(distanceMeters: number): number {
  if (distanceMeters <= FADE_FULL_METERS) return 1;
  if (distanceMeters >= FADE_MIN_METERS) return MIN_OPACITY;
  const t = (distanceMeters - FADE_FULL_METERS) / (FADE_MIN_METERS - FADE_FULL_METERS);
  return 1 - t * (1 - MIN_OPACITY);
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}
